import {Moment} from 'moment';

import {Request} from 'requests/RequestConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ProcessTaskForCalendarGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskForCalendarGraphQL';

const tzOffset = new Date().getTimezoneOffset() / -60;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCurrentOrgs(): JQueryPromise<any> {
  return net.getJSON('/1/api/users/me/organizations/');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getOrgRequests(): JQueryPromise<any> {
  return net.getJSON('/1/api/users/me/organization-requests/');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createInboundRequest(orgId: string): JQueryPromise<any> {
  return net.postJSON({
    url: '/1/api/users/me/organization-requests/',
    data: {organizationId: orgId},
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function acceptOutboundRequest(orgId: string): JQueryPromise<any> {
  return net.postJSON({
    url: `/1/api/users/me/organization-requests/${orgId}/actions/accept`,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function rejectOutboundRequest(orgId: string): JQueryPromise<any> {
  return net.postJSON({
    url: `/1/api/users/me/organization-requests/${orgId}/actions/reject`,
  });
}

export function getCalendarRequests(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
): JQueryPromise<{
  [dateString: string]: Array<Request>;
}> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/requests',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function getCalendarProcessTasks(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
): JQueryPromise<{
  [dateString: string]: Array<ProcessTaskForCalendarGraphQL>;
}> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/process-tasks',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function getCalendarEntries(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/entries',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function getCalendarFiles(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/files',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateCalendarEntryTask(data: any): JQueryPromise<any> {
  return net.postJSON({
    url: '/1/api/users/me/calendar/entries/actions/update-task',
    data,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getReferrals(): JQueryPromise<any> {
  return net.getJSON('/1/api/users/me/referrals');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPublicReferralId(email: string): JQueryPromise<any> {
  return net.getJSON('/1/api/public-referrals', {email});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createPublicReferralId(email: string): JQueryPromise<any> {
  return net.postJSON({url: '/1/api/public-referrals', data: {email}});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getMetadata(): JQueryPromise<any> {
  return net.getJSON('/1/api/users/me/metadata');
}

export function updateMetadata(data: {
  scientificField?: string | null;
  industry?: string | null;
  academicRole?: string | null;
  title?: string | null;
  teamName?: string | null;
  company?: string | null;
  phoneNumber?: string | null;
  institutionId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): JQueryPromise<any> {
  return net.patchJSON({url: '/1/api/users/me/metadata', data});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getReferralModals(): JQueryPromise<any> {
  return net.getJSON('/1/api/users/me/referral-modals');
}

export function acceptInstitutionTerms(userId: string): Promise<{}> {
  return net.postJSONPromise({url: `/1/api/users/${userId}:accept-institution-terms`});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fetch(userId: string): JQueryPromise<any> {
  return net.getJSON(`/1/api/users/${userId}`);
}

export function suspendUsers(isSuspendedByUserId: {[userId: string]: boolean}): Promise<{}> {
  return net.postJSONPromise({url: `/1/api/users:bulk-suspend`, data: {isSuspendedByUserId}});
}

export function updateUser(
  userId: string,
  userUpdateInfo: {name: string; handle: string; email: string}
): Promise<{}> {
  return net.patchJSONPromise({url: `/1/api/users/${userId}`, data: userUpdateInfo});
}

export function userURL(userId: string): string {
  return `/users/${userId}`;
}
